/**
 * 「AI 生成排版」弹窗。
 *
 * 流程刻意做成三步一屏：填 Key → （可选）传一张现有单据照片 → 生成，
 * 生成完直接在弹窗里渲染预览，确认了才落进模板库。
 *
 * 为什么一定要有预览这一步：AI 出的模板可能有幻觉字段（渲染出来是空白）或被
 * 自动修过的表格结构。让用户先看见成品再决定，比套用完再去编辑器里发现问题好。
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Typography, Upload } from 'antd';
import { InboxOutlined, ThunderboltOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';
import { buildContractHtml } from '../../render-core/renderer';
import { buildSampleContractData } from '../../render-core/sampleData';
import type { ContractTemplate } from '../contract/types';
import type { FieldMeta } from '../api/types';
import { track } from '../utils/track';
import { getBaseUserId } from '../api/bitable';
import {
  SAMPLE_ACCEPT,
  generateTemplate,
  loadApiKey,
  readSampleAsDataUrl,
  saveApiKey,
  type GenerateResult,
  type AiTrialQuota,
  fetchAiQuota,
} from '../ai/api';

interface Props {
  open: boolean;
  onClose: () => void;
  fieldMetas: FieldMeta[];
  tableName: string | null;
  /** 用户确认后把模板落库并进编辑器。 */
  onApply: (template: ContractTemplate) => void;
}

export function AiGenerateModal({ open, onClose, fieldMetas, tableName, onApply }: Props) {
  // 有多少人点开了 AI，又有多少人真的填了 Key —— 这两个数之差就是
  // 「要自备 apibobo Key」这道门槛挡掉的人
  const [quota, setQuota] = useState<AiTrialQuota>({ trialLimit: 0, trialUsed: 0, trialRemaining: 0 });
  const [accountId, setAccountId] = useState('');
  useEffect(() => {
    if (!open) return;
    track('ai_open');
    let cancelled = false;
    void (async () => {
      try {
        const id = await getBaseUserId();
        if (cancelled) return;
        setAccountId(id);
        const q = await fetchAiQuota(id);
        if (!cancelled) setQuota(q);
      } catch {
        /* 取不到就当没有免费额度，界面退回要求填 Key */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);
  const [apiKey, setApiKey] = useState(loadApiKey);
  const [file, setFile] = useState<UploadFile | null>(null);
  const [busy, setBusy] = useState<'' | 'reading' | 'generating'>('');
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<GenerateResult | null>(null);
  const objectUrl = useRef<string | null>(null);

  // 预览用示例数据：字段位置渲染成〔字段名〕占位，不依赖用户当前选没选记录
  const previewHtml = useMemo(() => {
    if (!result) return '';
    return buildContractHtml(result.template, buildSampleContractData(fieldMetas), 'preview');
  }, [result, fieldMetas]);

  const hallucinated = result?.issues.filter((i) => i.kind === 'hallucinated_field') ?? [];
  const repaired = result?.issues.filter((i) => i.kind === 'repaired_table') ?? [];

  const reset = () => {
    setResult(null);
    setError(null);
    if (objectUrl.current) {
      URL.revokeObjectURL(objectUrl.current);
      objectUrl.current = null;
    }
  };

  const handleClose = () => {
    reset();
    setFile(null);
    onClose();
  };

  const run = async () => {
    const key = apiKey.trim();
    // 没有免费额度、也没填 Key 才拦。以前一律要求自带 Key —— 那道墙比手工
    // 搭模板还高，等于把最需要 AI 的新用户挡在门外。
    if (!key && quota.trialRemaining <= 0) {
      setError(
        quota.trialLimit > 0
          ? `本月 ${quota.trialLimit} 次免费生成已用完，下月 1 日恢复。想继续用请填入 apibobo.com 的 API Key。`
          : '请先填入 apibobo.com 的 API Key',
      );
      return;
    }
    if (key) {
      saveApiKey(key);
      track('ai_key');
    }
    reset();
    try {
      let sample: { fileName: string; dataUrl: string } | undefined;
      const raw = file?.originFileObj as File | undefined;
      if (raw) {
        setBusy('reading');
        sample = { fileName: raw.name, dataUrl: await readSampleAsDataUrl(raw) };
      }
      setBusy('generating');
      const r = await generateTemplate({
        apiKey: key,
        accountId,
        tableName: tableName || '',
        fieldMetas,
        sample,
      });
      setResult(r);
      if (r.usedTrial && typeof r.trialRemaining === 'number') {
        setQuota((q) => ({ ...q, trialRemaining: r.trialRemaining as number }));
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy('');
    }
  };

  return (
    <Modal
      title={
        <Space size={8}>
          <ThunderboltOutlined style={{ color: 'var(--accent)' }} />
          <span>AI 生成排版</span>
        </Space>
      }
      open={open}
      onCancel={handleClose}
      width={620}
      footer={
        result ? (
          <Space>
            <Button onClick={() => setResult(null)}>重新生成</Button>
            <Button
              type="primary"
              onClick={() => {
                onApply(result.template);
                handleClose();
              }}
            >
              套用这份排版
            </Button>
          </Space>
        ) : (
          <Space>
            <Button onClick={handleClose}>取消</Button>
            <Button type="primary" loading={!!busy} onClick={run} disabled={fieldMetas.length === 0}>
              {busy === 'reading' ? '读取文件…' : busy === 'generating' ? '识别并排版中…' : '开始生成'}
            </Button>
          </Space>
        )
      }
    >
      {!result && (
        <>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 12 }}>
            AI 会读当前表的字段结构，自动判断这是什么单据并排好版。
            也可以上传一份<b>现有单据</b>（PDF / Word / 照片截图都行），让它照着仿版面。
          </Typography.Paragraph>

          {quota.trialRemaining > 0 && (
            <Alert
              type="success"
              showIcon
              style={{ marginBottom: 12 }}
              message={`本月还能免费生成 ${quota.trialRemaining} 次`}
              description="免费额度由我们承担，不用填任何 Key，直接点「开始生成」就行。"
            />
          )}

          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>
            API Key
            <Typography.Text type="secondary" style={{ fontWeight: 400, marginLeft: 6 }}>
              {quota.trialRemaining > 0
                ? '选填。填了就用你自己的额度，不占免费次数'
                : '在 apibobo.com 充值后获取，费用从你的账号扣'}
            </Typography.Text>
          </div>
          <Input.Password
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={quota.trialRemaining > 0 ? "留空即可，用免费额度" : "sk-..."}
            autoComplete="off"
            style={{ marginBottom: 4 }}
          />
          <Typography.Paragraph type="secondary" style={{ fontSize: 11, margin: '0 0 14px' }}>
            只保存在这台设备的浏览器里，不会随模板同步到云端。
          </Typography.Paragraph>

          <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 6 }}>
            单据样本（选填）
          </div>
          <Upload.Dragger
            accept={SAMPLE_ACCEPT}
            maxCount={1}
            fileList={file ? [file] : []}
            beforeUpload={() => false}
            onChange={({ fileList }) => setFile(fileList[0] ?? null)}
            onRemove={() => setFile(null)}
            style={{ background: 'var(--surface-2)', borderRadius: 8 }}
          >
            <p style={{ margin: '8px 0 4px' }}>
              <InboxOutlined style={{ fontSize: 22, color: 'var(--accent)' }} />
            </p>
            <p style={{ fontSize: 12.5, margin: 0 }}>把现有单据拖进来</p>
            <p style={{ fontSize: 11, color: 'var(--ink-3)', margin: '2px 0 8px' }}>
              支持 PDF / Word / 照片截图，不传也能生成
            </p>
          </Upload.Dragger>

          {fieldMetas.length === 0 && (
            <Alert
              style={{ marginTop: 12 }}
              type="warning"
              showIcon
              message="当前表还没读到字段，先刷新一下再试"
            />
          )}
          {error && <Alert style={{ marginTop: 12 }} type="error" showIcon message={error} />}
        </>
      )}

      {result && (
        <>
          <Space size={6} wrap style={{ marginBottom: 10 }}>
            <Typography.Text strong style={{ fontSize: 13 }}>
              {result.template.name}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 11.5 }}>
              {result.template.sections.length} 个区块 · {result.model}
            </Typography.Text>
          </Space>

          {hallucinated.length > 0 && (
            <Alert
              style={{ marginBottom: 10 }}
              type="warning"
              showIcon
              message="有几个字段在当前表里找不到"
              description={
                <span style={{ fontSize: 12 }}>
                  {hallucinated.map((i) => i.detail).join('、')}
                  ——这些位置会是空白，套用后可在编辑器里改绑其他字段。
                </span>
              }
            />
          )}
          {repaired.length > 0 && (
            <Alert
              style={{ marginBottom: 10 }}
              type="info"
              showIcon
              message="已自动修正表格结构"
              description={
                <span style={{ fontSize: 12 }}>{repaired.map((i) => i.detail).join('；')}</span>
              }
            />
          )}

          <div
            style={{
              border: '1px solid var(--border-soft)',
              borderRadius: 8,
              overflow: 'hidden',
              background: '#fff',
            }}
          >
            <iframe
              title="AI 排版预览"
              srcDoc={previewHtml}
              sandbox="allow-same-origin allow-scripts"
              style={{ width: '100%', height: 380, border: 0, display: 'block' }}
            />
          </div>
          <Typography.Paragraph type="secondary" style={{ fontSize: 11, margin: '8px 0 0' }}>
            预览里的〔字段名〕是占位，套用后会自动填入所选记录的真实值。
          </Typography.Paragraph>
        </>
      )}
    </Modal>
  );
}
